import { useMemo, useState, type FormEvent } from 'react';
import styled from '@emotion/styled';
import { normalizeEventName } from '@trafilea/tss-lib-funnel-schema-ts';
import type { StepPageBlock } from '../types';
import { useBlockEventEmitter } from '../events';

const NEWSLETTER_EMAIL_INPUT_NAME = 'newsletter-email';

function toCamelCase(value: string): string {
  return value.replace(/_([a-z])/g, (_, character: string) => character.toUpperCase());
}

function readStringProp(block: StepPageBlock, key: string, fallback: string): string {
  const value = block.props?.[key] ?? block.props?.[toCamelCase(key)];
  return typeof value === 'string' && value.trim() ? value : fallback;
}

function hasFormObservation(block: StepPageBlock): boolean {
  return Boolean(
    block.events?.onSubmit?.some((binding) => normalizeEventName(binding.name) === 'form_submitted')
  );
}

function selectSubmitBindings(block: StepPageBlock, selectFormObservation: boolean): StepPageBlock {
  return {
    ...block,
    events: {
      ...block.events,
      onSubmit: (block.events?.onSubmit ?? []).filter((binding) => {
        const isFormObservation = normalizeEventName(binding.name) === 'form_submitted';
        return isFormObservation === selectFormObservation;
      }),
    },
  };
}

const NewsletterWrap = styled.div<{ bgColor: string; textColor: string }>`
  padding: 32px 24px;
  background-color: ${({ bgColor }) => bgColor};
  text-align: center;
`;

const NewsletterHeadline = styled.h3<{ textColor: string }>`
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 700;
  color: ${({ textColor }) => textColor};
`;

const NewsletterDescription = styled.p<{ textColor: string }>`
  margin: 0 0 16px;
  font-size: 14px;
  opacity: 0.9;
  color: ${({ textColor }) => textColor};
`;

const NewsletterInputRow = styled.div`
  display: flex;
  gap: 8px;
  max-width: 360px;
  margin: 0 auto;
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 8px;
  max-width: 360px;
  margin: 0 auto;
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
`;

const NewsletterButton = styled.button<{ bgColor: string; textColor: string }>`
  padding: 10px 20px;
  background: ${({ textColor }) => textColor};
  color: ${({ bgColor }) => bgColor};
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
`;

export function NewsletterSignup({ block }: { block: StepPageBlock }) {
  const headline = readStringProp(block, 'headline', 'Stay in the Loop');
  const description = readStringProp(block, 'description', 'Get exclusive deals and early access.');
  const ctaText = readStringProp(block, 'cta_text', 'Subscribe');
  const bgColor = readStringProp(block, 'bg_color', '#005bd3');
  const textColor = readStringProp(block, 'text_color', '#ffffff');
  const [email, setEmail] = useState('');
  const blockEvents = useBlockEventEmitter(block);
  const observesValidFormSubmission = hasFormObservation(block);
  const formObservationBlock = useMemo(() => selectSubmitBindings(block, true), [block]);
  const localSubmitBlock = useMemo(() => selectSubmitBindings(block, false), [block]);
  const formObservationEvents = useBlockEventEmitter(formObservationBlock);
  const localSubmitEvents = useBlockEventEmitter(localSubmitBlock);

  const handleValidFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!event.currentTarget.checkValidity()) return;
    const emailInput = event.currentTarget.elements.namedItem(NEWSLETTER_EMAIL_INPUT_NAME);
    if (!(emailInput instanceof HTMLInputElement)) return;

    // Observation payloads intentionally omit the submitted address. The
    // trusted lead-capture default keeps its separate local value path below.
    formObservationEvents.onSubmit();
    localSubmitEvents.onSubmit(emailInput.value);
  };

  const controls = (
    <>
      <NewsletterInput
        type="email"
        placeholder="you@example.com"
        aria-label="Email address"
        autoComplete="email"
        name={NEWSLETTER_EMAIL_INPUT_NAME}
        required={observesValidFormSubmission}
        value={email}
        onChange={(event) => {
          setEmail(event.target.value);
          blockEvents.onChange(event.target.value);
        }}
      />
      <NewsletterButton
        type={observesValidFormSubmission ? 'submit' : 'button'}
        bgColor={bgColor}
        textColor={textColor}
        onClick={observesValidFormSubmission ? undefined : () => blockEvents.onSubmit(email)}
      >
        {ctaText}
      </NewsletterButton>
    </>
  );

  return (
    <NewsletterWrap bgColor={bgColor} textColor={textColor}>
      <NewsletterHeadline textColor={textColor}>{headline}</NewsletterHeadline>
      <NewsletterDescription textColor={textColor}>{description}</NewsletterDescription>
      {observesValidFormSubmission ? (
        <NewsletterForm aria-label="Newsletter signup" onSubmit={handleValidFormSubmit}>
          {controls}
        </NewsletterForm>
      ) : (
        <NewsletterInputRow>{controls}</NewsletterInputRow>
      )}
    </NewsletterWrap>
  );
}
